// Calories for a session from the 1 Hz telemetry rows (see
// useTelemetryRecorder). A machine that reports its own total energy (FTMS
// "expended energy", row.e) is taken at its word, so the app agrees with the
// number on the bike's or rower's console. Without it, the energy comes from
// the work done: kilojoules of work at the pedals are close to kilocalories
// burned (a gross efficiency of about 24 % cancels the 4.184 kJ per kcal).

const KCAL_PER_KJ_WORK = 1 / (4.184 * 0.24);

// A machine's running total (calories, distance, strokes) only climbs. It starts again from 0 when the machine
// restarts its workout or reconnects, and that drop is taken as a new count on
// top of the old one. A small dip is noise and ignored, so it can't count the
// whole total twice.
export const counterTotal = (values) => {
  let total = 0;
  let prev = null;
  for (const v of values) {
    if (prev == null) {
      total += v;
      prev = v;
    } else if (v >= prev) {
      total += v - prev;
      prev = v;
    } else if (v <= prev / 2) {
      total += v;
      prev = v;
    }
  }
  return total;
};

/**
 * Energy per machine type, from the telemetry rows.
 * @returns {Object<string, {kcal: number, source: "machine"|"power"}>}
 */
export function machineEnergyByDevice(samples) {
  if (!Array.isArray(samples) || samples.length === 0) return {};
  const byDev = {};
  for (const row of samples) {
    const dev = row?.dev;
    if (!dev) continue;
    const d = byDev[dev] || (byDev[dev] = { counter: [], joules: 0 });
    if (Number.isFinite(row.e) && row.e >= 0) d.counter.push(row.e);
    // One row a second, so watts are joules.
    if (Number.isFinite(row.w) && row.w > 0) d.joules += row.w;
  }
  const out = {};
  for (const [dev, d] of Object.entries(byDev)) {
    const counted = d.counter.length > 0 ? counterTotal(d.counter) : 0;
    if (counted > 0) out[dev] = { kcal: Math.round(counted), source: "machine" };
    else if (d.joules > 0) out[dev] = { kcal: Math.round((d.joules / 1000) * KCAL_PER_KJ_WORK), source: "power" };
  }
  return out;
}

/**
 * The session's machine calories, all machines together, or null when no
 * machine measured anything. `source` is "machine" only when every machine's
 * figure came from its own counter.
 */
export function sessionMachineEnergy(samples) {
  const parts = Object.values(machineEnergyByDevice(samples));
  if (parts.length === 0) return null;
  const kcal = parts.reduce((sum, p) => sum + p.kcal, 0);
  if (kcal <= 0) return null;
  return { kcal, source: parts.every((p) => p.source === "machine") ? "machine" : "power" };
}
